import { useMemo, useState } from 'react';
import {
  getActiveFlightsPairedWithCampaigns,
  getPastFlightsPairedWithCampaigns,
} from 'src/components/SponsoredListings';
import { useAdCampaignsDataContext } from 'src/contexts/AdCampaignsDataContext';
import { Content } from 'src/contexts/ContentContext/Content';
import { Stack } from 'src/core/ui';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  CampaignFlightsTable,
  CampaignsTableV1,
  PastFlightsTable,
} from 'src/tables/SponsoredListings';
import { ContentId } from 'src/utils/constants/contentId';
import {
  AdCampaignState,
  Event,
  Feature,
  Permission,
} from 'src/WebApiController';

import * as styles from '../InventoryEventPage.css';

export const InventoryEventBoostSection = ({ event }: { event?: Event }) => {
  const [isLoading, setIsLoading] = useState(() => false);

  const { adCampaignsDataQuery, allCampaignData, refreshData } =
    useAdCampaignsDataContext();

  const hasAdCampaignManagerFeature = useUserHasFeature(
    Feature.SponsoredListingsV2_CampaignManager
  );

  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );

  const allCampaigns = useMemo(() => {
    if (event?.viagId == null || !allCampaignData) {
      return null;
    }
    return allCampaignData[event?.viagId] ?? [];
  }, [allCampaignData, event?.viagId]);

  const activeCampaigns = useMemo(() => {
    if (allCampaigns == null) {
      return null;
    }
    return allCampaigns.filter(
      (c) => c.campaignState == AdCampaignState.Active && !c.hasEnded
    );
  }, [allCampaigns]);

  const inactiveCampaigns = useMemo(() => {
    if (allCampaigns == null) {
      return null;
    }
    return allCampaigns.filter(
      (c) => c.campaignState == AdCampaignState.Paused || c.hasEnded
    );
  }, [allCampaigns]);

  const campaignsWithActiveFlights = useMemo(
    () => getActiveFlightsPairedWithCampaigns(allCampaigns),
    [allCampaigns]
  );

  const campaignsWithPastFlights = useMemo(
    () => getPastFlightsPairedWithCampaigns(allCampaigns),
    [allCampaigns]
  );

  if (!hasAdPlatformViewPermission || event?.viagId == null) {
    return null;
  }

  return (
    <div className={styles.tableSection}>
      {hasAdCampaignManagerFeature ? (
        <>
          <Stack
            gap="m"
            direction="column"
            width="full"
            style={{
              height: campaignsWithActiveFlights?.length
                ? '50%'
                : 'fit-content',
            }}
          >
            <span>
              <Content id={ContentId.ActiveFlights} /> (
              {campaignsWithActiveFlights?.length ?? 0})
            </span>
            <CampaignFlightsTable
              disabled={isLoading}
              showEventColumns={false}
              isLoading={adCampaignsDataQuery.isLoading}
              campaignsAndFlights={campaignsWithActiveFlights}
              onStartWork={() => setIsLoading(true)}
              onStopWork={(success) => {
                setIsLoading(false);
                if (success) refreshData();
              }}
            />
          </Stack>
          <Stack
            gap="m"
            direction="column"
            width="full"
            style={{
              height: campaignsWithPastFlights?.length ? '50%' : 'fit-content',
            }}
          >
            <span>
              <Content id={ContentId.HistoricPerformance} /> (
              {campaignsWithPastFlights?.length ?? 0})
            </span>
            <CampaignFlightsTable
              disabled={isLoading}
              isLoading={adCampaignsDataQuery.isLoading}
              showEventColumns={false}
              groupByDate
              campaignsAndFlights={campaignsWithPastFlights}
              onStartWork={() => setIsLoading(true)}
              onStopWork={(success) => {
                setIsLoading(false);
                if (success) refreshData();
              }}
            />
          </Stack>
        </>
      ) : (
        <>
          <Stack
            gap="m"
            direction="column"
            width="full"
            style={{ height: activeCampaigns?.length ? '25%' : 'fit' }}
          >
            <span>
              <Content id={ContentId.ActiveCampaigns} /> (
              {activeCampaigns?.length ?? 0})
            </span>
            <CampaignsTableV1
              disabled={isLoading}
              isLoading={adCampaignsDataQuery.isLoading}
              campaigns={activeCampaigns}
              onStartWork={() => setIsLoading(true)}
              onStopWork={(success) => {
                setIsLoading(false);
                if (success) refreshData();
              }}
            />
          </Stack>
          <Stack
            gap="m"
            direction="column"
            width="full"
            style={{ height: inactiveCampaigns?.length ? '25%' : 'fit' }}
          >
            <span>
              <Content id={ContentId.InactiveCampaigns} /> (
              {inactiveCampaigns?.length ?? 0})
            </span>
            <CampaignsTableV1
              disabled={isLoading}
              isLoading={adCampaignsDataQuery.isLoading}
              campaigns={inactiveCampaigns}
              onStartWork={() => setIsLoading(true)}
              onStopWork={(success) => {
                setIsLoading(false);
                if (success) refreshData();
              }}
            />
          </Stack>
          <Stack
            gap="m"
            direction="column"
            width="full"
            style={{
              height: campaignsWithPastFlights?.length ? '50%' : 'fit',
            }}
          >
            <span>
              <Content id={ContentId.HistoricPerformance} /> (
              {campaignsWithPastFlights?.length ?? 0})
            </span>
            <PastFlightsTable
              isLoading={adCampaignsDataQuery.isLoading}
              campaignsAndFlights={campaignsWithPastFlights}
            />
          </Stack>
        </>
      )}
    </div>
  );
};
